import { useAnalytics } from "@client-shared/analytics";
import { useAdminProductUpdates } from "@client-shared/providers/admin-product-updates";
import type { WebStatus } from "@shared/services/pim/types";
import { CATALOGUE_REGIONS, type CatalogueRegionId, catalogueStorefrontChannel } from "@shared/types/admin-catalogue";
import {
  type AdminAttributeChange,
  type AdminAttributeField,
  type AdminProductUpdateResponse,
  type AdminSkuContext,
  type AdminTargetWebStatus,
  isCoffeeTerm,
} from "@shared/types/admin-product-update";
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import type { AdminCatalogueRow } from "./admin-catalogue-rows";

/** What the manager entered in the web status popover. */
export interface AdminStatusChangeValues {
  webStatus: AdminTargetWebStatus;
  effectiveDate: string;
  comments: string;
}

/** A row that has everything an approval request needs. */
export type EditableCatalogueRow = AdminCatalogueRow & { itemNumber: string; vendorKey: string; coffeeTerm: "Short" | "Long" };

/**
 * Why an edit is unavailable:
 * - `incomplete`: the SKU, or for a product-level attribute any sibling SKU, lacks the data a request needs;
 * - `noStorefrontStatus`: the SKU has no status on the region's storefront channel, which is what a change targets;
 * - `submitting` / `submitted`: a request is in flight or already raised this session, so a duplicate can't be sent.
 */
export type AdminEditLock = "incomplete" | "noStorefrontStatus" | "submitting" | "submitted";

export interface AdminProductEditContextValue {
  /** The product's SKUs that still need a request for this field: after a partial success, only the failed ones. */
  getPendingSkus: (productId: string, field: AdminAttributeField) => EditableCatalogueRow[];
  /** The change a partial success left unfinished: a retry must send the same value to the remaining SKUs. */
  getRetryChange: (productId: string, field: AdminAttributeField) => AdminAttributeChange | undefined;
  /** The SKU's status on the region's storefront channel, never another channel's. */
  getStorefrontWebStatus: (row: AdminCatalogueRow) => WebStatus | undefined;
  getAttributeLock: (row: AdminCatalogueRow, field: AdminAttributeField) => AdminEditLock | undefined;
  getStatusLock: (row: AdminCatalogueRow) => AdminEditLock | undefined;
  submitAttributeUpdate: (row: EditableCatalogueRow, change: AdminAttributeChange) => Promise<AdminProductUpdateResponse>;
  submitStatusChange: (row: EditableCatalogueRow, values: AdminStatusChangeValues) => Promise<AdminProductUpdateResponse>;
}

const AdminProductEditContext = createContext<AdminProductEditContextValue | null>(null);

/** Null outside the admin catalogue page, where the cells stay read-only. */
export const useAdminProductEdit = () => useContext(AdminProductEditContext);

export const isEditableRow = (row: AdminCatalogueRow): row is EditableCatalogueRow =>
  Boolean(row.itemNumber && row.vendorKey && isCoffeeTerm(row.coffeeTerm));

const toSkuContext = (row: EditableCatalogueRow): AdminSkuContext => ({ itemNumber: row.itemNumber, coffeeTerm: row.coffeeTerm });

const productContext = (region: CatalogueRegionId, row: EditableCatalogueRow) => ({
  region,
  vendorKey: row.vendorKey,
  productName: row.title ?? row.itemNumber,
});

/** Every SKU row of each product, complete or not: a product-level edit must cover all of them or none. */
const groupSkusByProduct = (rows: readonly AdminCatalogueRow[]): Map<string, AdminCatalogueRow[]> => {
  const groups = new Map<string, AdminCatalogueRow[]>();
  rows.filter((row) => row.itemNumber).forEach((row) => groups.set(row.productId, [...(groups.get(row.productId) ?? []), row]));
  return groups;
};

type EditedField = AdminAttributeField | "webStatus";

// Created requests are keyed by SKU, so a partial success leaves the failed SKUs open for a retry.
const skuKey = (region: CatalogueRegionId, itemNumber: string, field: EditedField) => `${region}:${itemNumber}:${field}`;
// In-flight requests and unfinished changes are keyed by what the editor edits: a product's attribute, or one SKU's status.
const attributeKey = (region: CatalogueRegionId, productId: string, field: AdminAttributeField) => `${region}:${productId}:${field}`;

/** Marks a key in flight for the duration of a request, readable synchronously so a double submit is refused. */
const useInFlight = () => {
  const inFlightRef = useRef(new Set<string>());
  const [inFlight, setInFlight] = useState<ReadonlySet<string>>(() => new Set());
  const run = useCallback(async <T,>(key: string, request: () => Promise<T>): Promise<T> => {
    if (inFlightRef.current.has(key)) throw new Error("This change is already being submitted");
    inFlightRef.current.add(key);
    setInFlight(new Set(inFlightRef.current));
    try {
      return await request();
    } finally {
      inFlightRef.current.delete(key);
      setInFlight(new Set(inFlightRef.current));
    }
  }, []);
  return { inFlight, run };
};

interface AdminProductEditProviderProps {
  readonly region: CatalogueRegionId;
  readonly rows: readonly AdminCatalogueRow[];
  readonly children: ReactNode;
}

/**
 * Turns the catalogue's edits into admin approval requests: attribute changes go to every SKU of the product, a web
 * status change to the one SKU. Approval happens in Salesforce, so the catalogue keeps showing the current value; each
 * SKU's edited field is locked until the page is reloaded instead. State is kept per region, so switching region and
 * back keeps it.
 */
export function AdminProductEditProvider({ region, rows, children }: AdminProductEditProviderProps) {
  const { submitAttributeUpdate, submitStatusChange } = useAdminProductUpdates();
  const analytics = useAnalytics();
  const [created, setCreated] = useState<ReadonlySet<string>>(() => new Set());
  const [retryChanges, setRetryChanges] = useState<ReadonlyMap<string, AdminAttributeChange>>(() => new Map());
  const { inFlight, run } = useInFlight();
  const skusByProduct = useMemo(() => groupSkusByProduct(rows), [rows]);
  const storefrontChannel = catalogueStorefrontChannel(CATALOGUE_REGIONS[region]);

  const markCreated = useCallback(
    (response: AdminProductUpdateResponse, field: EditedField) =>
      setCreated((current) => {
        const next = new Set(current);
        response.results.forEach((result) => next.add(skuKey(region, result.itemNumber, field)));
        return next;
      }),
    [region]
  );

  // Pins the value a partial success sent, so the retry can't send a different one to the remaining SKUs.
  const recordRetry = useCallback((key: string, change: AdminAttributeChange, response: AdminProductUpdateResponse) => {
    setRetryChanges((current) => {
      const next = new Map(current);
      if (response.failures.length > 0) next.set(key, change);
      else next.delete(key);
      return next;
    });
  }, []);

  const value = useMemo<AdminProductEditContextValue>(() => {
    const siblingsOf = (productId: string) => skusByProduct.get(productId) ?? [];
    const getPendingSkus = (productId: string, field: AdminAttributeField) =>
      siblingsOf(productId)
        .filter(isEditableRow)
        .filter((sku) => !created.has(skuKey(region, sku.itemNumber, field)));
    const getStorefrontWebStatus = (row: AdminCatalogueRow) =>
      row.shipToInfo?.find((info) => info.channel === storefrontChannel)?.webStatus;

    return {
      getPendingSkus,
      getRetryChange: (productId, field) => retryChanges.get(attributeKey(region, productId, field)),
      getStorefrontWebStatus,
      getAttributeLock: (row, field) => {
        const siblings = siblingsOf(row.productId);
        if (siblings.length === 0 || !siblings.every(isEditableRow)) return "incomplete";
        if (inFlight.has(attributeKey(region, row.productId, field))) return "submitting";
        return getPendingSkus(row.productId, field).length === 0 ? "submitted" : undefined;
      },
      getStatusLock: (row) => {
        if (!isEditableRow(row)) return "incomplete";
        if (!getStorefrontWebStatus(row)) return "noStorefrontStatus";
        if (inFlight.has(skuKey(region, row.itemNumber, "webStatus"))) return "submitting";
        return created.has(skuKey(region, row.itemNumber, "webStatus")) ? "submitted" : undefined;
      },
      submitAttributeUpdate: (row, change) => {
        const key = attributeKey(region, row.productId, change.field);
        return run(key, async () => {
          const pinned = retryChanges.get(key) ?? change;
          const skus = getPendingSkus(row.productId, change.field).map(toSkuContext);
          if (skus.length === 0) throw new Error("Every SKU of this product already has a request for this change");
          const response = await submitAttributeUpdate({ ...productContext(region, row), change: pinned, skus });
          markCreated(response, change.field);
          recordRetry(key, pinned, response);
          analytics.track("BCC Admin Catalogue / Attribute Update Submitted", {
            field: change.field,
            catalogueRegion: region,
            skuCount: skus.length,
            createdCount: response.results.length,
            failedCount: response.failures.length,
          });
          return response;
        });
      },
      submitStatusChange: (row, values) =>
        run(skuKey(region, row.itemNumber, "webStatus"), async () => {
          const currentWebStatus = getStorefrontWebStatus(row);
          if (!currentWebStatus) throw new Error("This SKU has no status on the region's storefront channel");
          const response = await submitStatusChange({
            ...productContext(region, row),
            ...values,
            sku: toSkuContext(row),
            currentWebStatus,
          });
          markCreated(response, "webStatus");
          analytics.track("BCC Admin Catalogue / Status Change Submitted", {
            catalogueRegion: region,
            fromStatus: currentWebStatus,
            toStatus: values.webStatus,
          });
          return response;
        }),
    };
  }, [
    region,
    skusByProduct,
    storefrontChannel,
    created,
    retryChanges,
    inFlight,
    run,
    submitAttributeUpdate,
    submitStatusChange,
    markCreated,
    recordRetry,
    analytics,
  ]);

  return <AdminProductEditContext.Provider value={value}>{children}</AdminProductEditContext.Provider>;
}
